import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Camera, Globe, Heart, ArrowRight, Plane } from 'lucide-react';
import { VentanaLogo } from '@/components/VentanaLogo';

export default function Landing() {
  const { isAuthenticated, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/feed');
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    {
      icon: Camera,
      title: 'Share Your Views',
      description: 'Upload stunning window seat photos from your flights around the world.'
    },
    {
      icon: Plane,
      title: 'Tag Your Flight',
      description: 'Add airline, aircraft type, route, and seat details to your captures.'
    },
    {
      icon: Globe,
      title: 'Explore the World',
      description: 'Discover breathtaking views from travelers on an interactive 3D globe.'
    },
    {
      icon: Heart,
      title: 'Connect & Inspire',
      description: 'Like and engage with fellow aviation enthusiasts and window gazers.'
    }
  ];

  const heroImages = [
    'https://images.unsplash.com/photo-1565444007614-6b38c78224df?w=800&q=80',
    'https://images.unsplash.com/photo-1720495099078-833f01d1f9a6?w=800&q=80',
    'https://images.unsplash.com/photo-1678485652523-cabdeb24a6a3?w=800&q=80'
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImages[0]}
            alt="Airplane window sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05050A] via-[#05050A]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-[#05050A]/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              <span className="text-white">Share the view from</span>
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                30,000 feet
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-300 mb-8 animate-fade-in-up animate-delay-100">
              Join a community of travelers capturing breathtaking moments from airplane windows. 
              Tag your flights, discover stunning views, and explore the world from above.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-200">
              <Button 
                onClick={login}
                className="btn-primary-gradient rounded-full px-8 py-6 text-lg font-medium"
                data-testid="hero-cta-btn"
              >
                Start Sharing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/explore')}
                className="btn-secondary rounded-full px-8 py-6 text-lg font-medium text-white"
                data-testid="explore-btn"
              >
                Explore Views
              </Button>
            </div>
          </div>
        </div>

        {/* Floating preview images */}
        <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[500px]">
          <div className="relative">
            <div className="absolute -top-20 right-40 w-48 h-64 rounded-2xl overflow-hidden glass-card glow-primary animate-fade-in-up animate-delay-200">
              <img src={heroImages[1]} alt="Window view" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-20 right-0 w-56 h-72 rounded-2xl overflow-hidden glass-card glow-secondary animate-fade-in-up animate-delay-300">
              <img src={heroImages[2]} alt="Window view" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Your Window to the World
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Ventana brings together aviation enthusiasts and travel lovers in one beautiful space.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="glass-card rounded-2xl p-6 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 
                  className="text-lg font-semibold text-white mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Discover Amazing Views
            </h2>
            <p className="text-gray-400">Curated captures from around the globe</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'https://images.unsplash.com/photo-1565444007614-6b38c78224df?w=600&q=80',
              'https://images.unsplash.com/photo-1720495099078-833f01d1f9a6?w=600&q=80',
              'https://images.unsplash.com/photo-1664190426349-d1a9c2e52fd7?w=600&q=80',
              'https://images.unsplash.com/photo-1678485652523-cabdeb24a6a3?w=600&q=80'
            ].map((img, i) => (
              <div 
                key={i} 
                className={`rounded-2xl overflow-hidden ${i === 1 ? 'row-span-2' : ''}`}
              >
                <img 
                  src={img} 
                  alt={`Gallery ${i + 1}`} 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/explore')}
              className="btn-secondary rounded-full px-8 py-4 text-white"
            >
              View All Photos
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-card rounded-3xl p-12 glow-primary">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Ready to share your view?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Join thousands of travelers documenting their journeys from above. 
              Your next window seat photo could inspire someone's next adventure.
            </p>
            <Button 
              onClick={login}
              className="btn-primary-gradient rounded-full px-10 py-6 text-lg font-medium"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <VentanaLogo size="small" />
            <span className="text-gray-400 text-sm">© 2025</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <span>Made for aviation lovers</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { Landing };
